/* ══════════════════════════════════════════════════════════════════════════
 * Create Class form — preview shirt colours.
 *
 * The colours are the ones ENABLED on the DK Royal Hoodie (its Royal-collection
 * variation, which is what the product page sells), read live from the API so
 * the admin's attribute changes reach the form; the two historic colours stay
 * as a fallback while loading / when the API is unreachable.
 *
 * tint = the fabric colour the 3D body renders: the calibrated palette tint when
 * the colour's hex is a palette swatch, else the hex itself. Ink proposal per
 * palette.autoInk: dark fabric → white ink, light → black.
 * ══════════════════════════════════════════════════════════════════════════ */
import palette from '@ajat/design-system/Product3DPreview/config/palette.json';

export const PREVIEW_BRAND = 'clsdk';
export const PREVIEW_PRODUCT_SLUG = 'royal-hoodie';
/** Royal Hoodie in the Royal collection (PROD and DEV variation id). */
export const PREVIEW_VARIATION_ID = 135;

export const FALLBACK_COLORS = [
  { id: 'blue',  name: 'Mørk', swatch: '#495c6d', tint: '#1c2b3b', dark: true },
  { id: 'beige', name: 'Lys',  swatch: '#e1cfb7', tint: '#d9d0b0', dark: false },
];

// The Royal Hoodie: the hoodie's colour list (palette.colors is per garment since 2026-10-29)
const HOODIE_COLORS = palette.colors?.hoodie || [];
const TINT_BY_SWATCH = Object.fromEntries(HOODIE_COLORS.map((c) => [String(c.swatch).toLowerCase(), c.tint]));
const NAME_BY_SWATCH = Object.fromEntries(HOODIE_COLORS.map((c) => [String(c.swatch).toLowerCase(), c.name]));
const THRESHOLD = palette.autoInk?.luminanceThreshold ?? 140;

/** Perceived luminance 0–255 of a #rrggbb hex; null when not a hex. */
export function luminance(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return 0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
}

export const isDark = (hex) => { const l = luminance(hex); return l == null ? false : l < THRESHOLD; };

/** Ink id proposed for a shirt colour ({dark} or hex). */
export function inkForColor(color) {
  const dark = typeof color === 'object' && color ? !!color.dark : isDark(color);
  return dark ? (palette.autoInk?.dark ?? 'white') : (palette.autoInk?.light ?? 'black');
}

/** One form colour from an API colour value {id, value, rawValue}. */
export function colorFromValue(v) {
  const hex = String(v?.rawValue || '').trim();
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return null;
  const key = hex.toLowerCase();
  return {
    id: String(v.id),
    name: v.value && !/^#/.test(String(v.value)) ? String(v.value) : (NAME_BY_SWATCH[key] || hex.toUpperCase()),
    swatch: hex,
    tint: TINT_BY_SWATCH[key] || hex,
    dark: isDark(hex),
  };
}

/** The form colours of a variation payload (bare PDP ?variation= shape); [] when it has no colour attribute. */
export function colorsFromVariation(payload) {
  const attrs = [...(payload?.pinned_attributes || []), ...(payload?.available_options || [])];
  const color = attrs.find((a) => a?.slug === 'color' || a?.type === 'color');
  return (color?.values || []).map(colorFromValue).filter(Boolean);
}
